import React, { useEffect, useState } from 'react';
import {
  Modal,
  Box,
  IconButton,
  Card,
  CardMedia,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { darken } from '@mui/material/styles';

interface FullSizePreviewProps {
  open: boolean;
  onClose: () => void;
  imageUrl: string | null;
  isLoading: boolean;
  isPDF?: boolean;
}

const FullSizePreview: React.FC<FullSizePreviewProps> = ({
  open,
  onClose,
  imageUrl,
  isLoading,
  isPDF = false,
}) => {
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    if (open && imageUrl && !isPDF) {
      setImageLoading(true);
      setImageError(false);
    }
  }, [imageUrl, open, isPDF]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Box sx={{ position: 'relative' }}>
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            zIndex: 1,
            bgcolor: 'rgba(255, 255, 255, 0.9)',
            boxShadow: '0 2px 3px rgba(0,0,0,0.2)',
            '&:hover': {
              bgcolor: 'white',
              transform: 'scale(1.1)',
            },
            transition: 'all 0.2s ease',
          }}
        >
          <CloseIcon />
        </IconButton>
        <Card
          elevation={8}
          sx={{
            position: 'relative',
            maxWidth: '90vw',
            maxHeight: '90vh',
            borderRadius: 2,
            overflow: 'hidden',
            p: 1,
            bgcolor: 'rgba(255, 255, 255, 0.98)',
          }}
        >
          {isPDF ? (
            <Box
              sx={{
                position: 'relative',
                width: '70vw',
                height: '85vh',
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              {isLoading ? (
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : (
                <iframe
                  src={imageUrl + '#toolbar=0&view=FitH'}
                  title="PDF document"
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    borderRadius: '8px',
                  }}
                />
              )}
            </Box>
          ) : (
            <>
              {!imageError ? (
                <CardMedia
                  component="img"
                  src={imageUrl || ''}
                  alt="File full size"
                  sx={{
                    maxWidth: '90vw',
                    maxHeight: '90vh',
                    objectFit: 'contain',
                    borderRadius: 2,
                    transition: 'opacity 0.3s ease-in-out',
                    opacity: imageLoading ? 0.7 : 1,
                  }}
                  onLoad={() => setImageLoading(false)}
                  onError={() => {
                    setImageLoading(false);
                    setImageError(true);
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: '60vw',
                    height: '60vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#888',
                    fontSize: '1.2rem',
                  }}
                >
                  Failed to load image.
                </Box>
              )}
            </>
          )}
          {!isPDF && imageLoading && !imageError && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(0, 0, 0, 0.1)',
                transition: 'opacity 0.3s ease-in-out',
                borderRadius: 2,
                zIndex: 2,
              }}
            >
              <CircularProgress
                size={48}
                sx={{
                  color: 'primary.main',
                  opacity: 0.8,
                }}
              />
            </Box>
          )}
        </Card>
      </Box>
    </Modal>
  );
};

export default FullSizePreview;
